import React, { useState, useEffect, useRef } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp, getDocs, doc, getDoc, updateDoc, query, orderBy, limit as firestoreLimit, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { storage, db, functions } from '../../firebaseInit';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorMessage from '../shared/ErrorMessage';
import Button from '../shared/Button';
import { getCallTypes, formatUserName } from '../../utils/industryConfig';
import { useData } from '../../contexts/DataContext';
import { getUsageStatus, getCurrentPeriod, shouldResetUsage, getInitialUsageData, getInitialSubscription, formatCost, ADDONS, SUBSCRIPTION_STATUSES, getSubscriptionStatusDisplay } from '../../utils/usageTracking';
import { purchaseEvalCredits, isStripeConfigured } from '../../utils/stripe';
import { createAutoReport } from '../../utils/autoProblemReport';
import { colors } from '../../styles/designTokens';

const DEBUG_UPLOAD = process.env.NODE_ENV === 'development';
const uploadDebug = (...args) => { if (DEBUG_UPLOAD) console.log(...args); };

export default function AudioUploader() {
  // userTeam now auto-switches when admin views another team
  const { userEmail, loading, userId, userRole, userTeam, userTeamName } = useAuth();
  const { teamData, userDefaults } = useData();
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  const isBasicUser = userRole === 'basic';
  const industryId = teamData?.industryId;
  const callTypes = getCallTypes(industryId);
  
  uploadDebug('🎤 AudioUploader -', { isBasicUser, userRole });
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formData, setFormData] = useState({
    salesperson: '',
    prospect: '',
    callType: callTypes[0]?.value || 'outbound',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadMembersError, setLoadMembersError] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [processingStage, setProcessingStage] = useState(0);
  const [linkedLeadId, setLinkedLeadId] = useState('');
  const [recentLeads, setRecentLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const processingTimerRef = useRef(null);
  
  // Load team usage and subscription data
  useEffect(() => {
    async function loadUsageData() {
      if (!userTeam) {
        return;
      }

      try {
        const teamPath = `artifacts/my-real-estate-assistant/teams/${userTeam}`;
        const teamRef = doc(db, teamPath);
        const teamDoc = await getDoc(teamRef);

        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          let sub = teamData.subscription || getInitialSubscription('starter');
          let usage = teamData.usage || getInitialUsageData(sub.plan);

          // Check if we need to reset usage for new month
          const currentPeriod = getCurrentPeriod();
          if (shouldResetUsage(currentPeriod, usage.currentPeriod)) {
            console.log('New billing period detected, resetting usage');
            usage = getInitialUsageData(sub.plan);
            
            // Update in Firestore
            await updateDoc(teamRef, {
              usage: usage
            });
          }

          setSubscription(sub);
          setUsageData(usage);
        } else {
          // Initialize defaults for new team
          const defaultSub = getInitialSubscription('starter');
          const defaultUsage = getInitialUsageData('starter');
          
          await updateDoc(teamRef, {
            subscription: defaultSub,
            usage: defaultUsage
          });
          
          setSubscription(defaultSub);
          setUsageData(defaultUsage);
        }
      } catch (error) {
        console.error('Error loading usage data:', error);
      } finally {
        setLoadingUsage(false);
      }
    }

    loadUsageData();
  }, [userTeam]);
  
  // Auto-select current user for basic users
  useEffect(() => {
    if (isBasicUser && userEmail) {
      uploadDebug('✅ Auto-selecting current user for basic user');
      setFormData(prev => ({ ...prev, salesperson: userEmail }));
    }
  }, [isBasicUser, userEmail]);
  
  // Load team members (all active users from the team)
  useEffect(() => {
    let mounted = true;
    let timeoutId;
    
    async function loadTeamMembers() {
      // Basic users don't need to load team members (they can only upload for themselves)
      if (isBasicUser) {
        setLoadingMembers(false);
        return;
      }
      
      if (!userTeam) {
        console.log('Waiting for userTeam to load...');
        // Set timeout to stop loading after 5 seconds if userTeam never loads
        timeoutId = setTimeout(() => {
          if (mounted) {
            console.warn('⚠️ Timeout: userTeam not loaded after 5s');
            setLoadingMembers(false);
          }
        }, 5000);
        return;
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      console.log('Loading team members for team:', userTeam);

      try {
        const teamUsersPath = `artifacts/my-real-estate-assistant/teams/${userTeam}/users`;
        console.log('Fetching from path:', teamUsersPath);
        
        const usersSnapshot = await getDocs(collection(db, teamUsersPath));
        
        console.log('Found team members:', usersSnapshot.docs.length);
        
        if (!mounted) return;
        
        const members = usersSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(user => user.status === 'active') // Only active users
          .sort((a, b) => {
            return formatUserName(a).localeCompare(formatUserName(b));
          });
        
        console.log('Active team members:', members.length);
        setTeamMembers(members);
      } catch (error) {
        console.error('❌ Error loading team members:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        if (mounted) {
          // Set empty array so dropdown shows "no members" instead of hanging
          setTeamMembers([]);
          setLoadMembersError(error.message || 'Failed to load team members');
        }
      } finally {
        if (mounted) {
          setLoadingMembers(false);
        }
      }
    }
    
    loadTeamMembers();
    
    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [userTeam, isBasicUser]);

  // Load recent leads for linking (filtered by selected salesperson)
  useEffect(() => {
    if (!userTeam || !db) return;
    
    let mounted = true;
    const loadRecentLeads = async () => {
      setLoadingLeads(true);
      try {
        // Load leads from team members' realEstateLeads subcollections
        const teamUsersPath = `artifacts/my-real-estate-assistant/teams/${userTeam}/users`;
        const usersSnapshot = await getDocs(collection(db, teamUsersPath));
        const allLeads = [];
        
        for (const userDoc of usersSnapshot.docs) {
          const leadsPath = `${teamUsersPath}/${userDoc.id}/realEstateLeads`;
          const leadsQuery = query(
            collection(db, leadsPath),
            orderBy('timestamp', 'desc'),
            firestoreLimit(20)
          );
          const leadsSnapshot = await getDocs(leadsQuery);
          leadsSnapshot.docs.forEach(leadDoc => {
            const data = leadDoc.data();
            allLeads.push({
              id: leadDoc.id,
              path: `${leadsPath}/${leadDoc.id}`,
              userDocId: userDoc.id,
              ...data,
              timestamp: data.timestamp?.toDate?.() || (data.timestamp ? new Date(data.timestamp) : null)
            });
          });
        }
        
        // Sort by timestamp descending
        allLeads.sort((a, b) => (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0));
        
        if (mounted) {
          setRecentLeads(allLeads.slice(0, 50));
        }
      } catch (error) {
        console.warn('Failed to load leads for linking:', error);
      } finally {
        if (mounted) setLoadingLeads(false);
      }
    };
    
    loadRecentLeads();
    return () => { mounted = false; };
  }, [userTeam]);

  const validateAndSetFile = (file) => {
    if (!file) return false;
    
    // Validate file type
    const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/x-m4a', 'audio/m4a', 'audio/mp4'];
    const validExtensions = /\.(mp3|wav|m4a)$/i;
    
    if (!validTypes.includes(file.type) && !file.name.match(validExtensions)) {
      showError('Please upload an audio file (MP3, WAV, or M4A)');
      return false;
    }
    
    // Validate file size (max 100MB for longer calls)
    if (file.size > 100 * 1024 * 1024) {
      showError('File size must be less than 100MB');
      return false;
    }
    
    setSelectedFile(file);
    setStatusMessage('');
    return true;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    validateAndSetFile(file);
  };

  // Processing stages configuration
  const PROCESSING_STAGES = [
    { label: 'Transcribing audio...', icon: '🎙️', range: [0, 30] },
    { label: 'Analyzing conversation...', icon: '🔍', range: [30, 60] },
    { label: 'Generating evaluation...', icon: '📝', range: [60, 90] },
    { label: 'Complete!', icon: '✅', range: [90, 100] },
  ];

  // Advance processing stages based on time elapsed
  useEffect(() => {
    if (processing) {
      setProcessingStage(0);
      // Stage 0 → 1 after 4s, 1 → 2 after 10s, stays at 2 until complete
      const timer1 = setTimeout(() => setProcessingStage(1), 4000);
      const timer2 = setTimeout(() => setProcessingStage(2), 12000);
      processingTimerRef.current = [timer1, timer2];
    } else {
      // Cleanup timers when processing ends
      if (processingTimerRef.current) {
        processingTimerRef.current.forEach(t => clearTimeout(t));
        processingTimerRef.current = null;
      }
      setProcessingStage(0);
    }
    return () => {
      if (processingTimerRef.current) {
        processingTimerRef.current.forEach(t => clearTimeout(t));
        processingTimerRef.current = null;
      }
    };
  }, [processing]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    if (uploading || processing) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  const handleDropZoneClick = () => {
    if (!uploading && !processing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const removeSelectedFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile || !formData.salesperson || !formData.prospect) {
      showWarning('Please fill in all fields and select an audio file');
      return;
    }
    
    if (!userEmail && !userId) {
      showError('You must be logged in to upload calls. Please refresh the page and try again.');
      return;
    }

    // Check usage limits
    if (subscription && usageData) {
      const bonusCredits = subscription.addons?.bonusEvalCredits || 0;
      const usageStatus = getUsageStatus(
        subscription.plan,
        usageData.evaluationsUsed,
        bonusCredits,
        subscription.status
      );

      if (!usageStatus.canUpload) {
        showWarning(
          `Monthly limit reached (${usageData.evaluationsUsed}/${usageStatus.limit} evaluations used). ` +
          `Purchase more credits or upgrade your plan in Settings → Subscription.`
        );
        return;
      }

      // Warn when running low
      if (usageStatus.remaining <= 2 && usageStatus.remaining > 0) {
        showInfo(`You have ${usageStatus.remaining} evaluation${usageStatus.remaining !== 1 ? 's' : ''} remaining this month.`);
      }
    }
    
    // Use userId (already available from auth)
    const userIdToUse = userId;
    
    setUploading(true);
    setStatusMessage('Uploading audio file...');
    
    // Force token refresh to ensure latest permissions are applied
    try {
      const { auth } = await import('../../firebaseInit');
      const currentUser = auth.currentUser;
      if (currentUser) {
        uploadDebug('🔄 Refreshing auth token...');
        await currentUser.getIdToken(true);
        uploadDebug('✅ Token refreshed');
      }
    } catch (tokenError) {
      console.warn('⚠️ Token refresh failed, continuing anyway:', tokenError);
    }
    
    try {
      // Get salesperson details
      // For basic users, they're uploading their own calls, so use their info directly
      const selectedMember = isBasicUser 
        ? { email: userEmail, role: userRole, userId: userId, defaultTemplate: userDefaults?.defaultTemplate }
        : teamMembers.find(m => m.email === formData.salesperson);

      // Create evaluation document in team container
      const collectionPath = `artifacts/my-real-estate-assistant/teams/${userTeam}/call-evaluations`;
      console.log('Creating evaluation in team container:', collectionPath);
            
      // Prepare the document data
      const documentData = {
        salesperson: formData.salesperson,
        salespersonName: selectedMember ? formatUserName(selectedMember) : formData.salesperson,
        salespersonRole: selectedMember?.role || userRole || 'unknown',
        salespersonUserId: selectedMember ? (selectedMember.userId || userIdToUse) : null,
        isTeamMember: !!selectedMember,
        prospect: formData.prospect,
        callType: formData.callType,
        industry: industryId || 'general',
        uploadedBy: userIdToUse,
        uploadedByEmail: userEmail || 'unknown@example.com',
        uploadedByRole: userRole,
        teamId: userTeam,
        teamName: userTeamName || 'Unknown Team', // TODO: Get team name from activeTeamId
        // Track which script the salesperson had assigned at upload time.
        // Used to link evaluations back to a specific script for AI Improve analysis.
        assignedTemplateId: selectedMember?.defaultTemplate || null,
        status: 'uploading',
        uploadedAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), // 90 days
        linkedLeadId: linkedLeadId || null
      };
            
      console.log('📝 Document data to write:', {
        ...documentData,
        uploadedAt: '[serverTimestamp()]',
        salesperson: documentData.salesperson,
        uploadedBy: documentData.uploadedBy,
        teamId: documentData.teamId
      });
      console.log('🔑 Current auth state:', {
        userId: userId,
        userEmail: userEmail,
        userRole: userRole,
        userTeam: userTeam
      });
            
      const evaluationRef = await addDoc(
        collection(db, collectionPath),
        documentData
      );
      
      console.log('✅ Created evaluation document successfully:', evaluationRef.id);
      console.log('Document path:', `${collectionPath}/${evaluationRef.id}`);

      // If linked to a lead, update the lead document with the evaluation ID
      if (linkedLeadId) {
        try {
          const linkedLead = recentLeads.find(l => l.id === linkedLeadId);
          if (linkedLead?.path) {
            await updateDoc(doc(db, linkedLead.path), { linkedEvaluationId: evaluationRef.id });
            console.log('✅ Linked lead updated with evaluation ID:', evaluationRef.id);
          }
        } catch (linkError) {
          console.warn('⚠️ Failed to link evaluation to lead (non-critical):', linkError);
        }
      }
      
      // Upload audio file to Storage
      const storagePath = `call-evaluations/${userIdToUse}/${evaluationRef.id}/${selectedFile.name}`;
      uploadDebug('📤 Uploading to storage, evalId:', evaluationRef.id);
      console.log('📤 File name:', selectedFile.name);
      
      const storageRef = ref(storage, storagePath);
      
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(prog);
          setStatusMessage(`Uploading... ${Math.round(prog)}%`);
        },
        (error) => {
          console.error('❌ Storage upload error:', error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          console.error('Storage path:', storagePath);
          
          // User-friendly error message (technical details captured in auto-report)
          const userMessage = error.code === 'storage/unauthorized'
            ? 'Upload failed — your session may have expired. Please refresh the page and try again.'
            : 'Something went wrong uploading your file. Please try again.';
          
          showError(userMessage);
          setUploading(false);
          setStatusMessage('');
          createAutoReport({
            errorType: 'evaluation_upload_failed',
            errorMessage: error.message || error.code || 'Storage upload error',
            userEmail,
            userRole,
            teamId: userTeam,
            context: {
              errorCode: error.code,
              fileName: selectedFile?.name,
              fileSize: selectedFile?.size,
              callType: formData.callType,
              salesperson: formData.salesperson,
            },
          });
        },
        async () => {
          // Upload complete, get download URL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          console.log('Upload complete, download URL:', downloadURL);
          
          setStatusMessage('Upload complete! Starting transcription...');
          setUploading(false);
          setProcessing(true);
          
          // Trigger Cloud Function for processing
          // The Cloud Function will update the document with audioUrl and handle status changes
          try {
            const processCallEvaluation = httpsCallable(functions, 'processCallEvaluation');
            const result = await processCallEvaluation({
              evaluationId: evaluationRef.id,
              audioUrl: downloadURL,
              audioFileName: selectedFile.name,
              teamId: userTeam
            });
            
            console.log('Processing result:', result);
            
            // Show "Complete!" stage briefly before resetting
            setProcessingStage(3);
            setStatusMessage('✅ Evaluation completed successfully!');
            await new Promise(resolve => setTimeout(resolve, 1000));
            setProcessing(false);
            
            // Reset form
            setFormData({
              salesperson: '',
              prospect: '',
              callType: callTypes[0]?.value || 'outbound',
            });
            setSelectedFile(null);
            setProgress(0);
            
            // Show success message
            showSuccess('Call evaluation completed! View it in the Evaluations tab.');
          } catch (processingError) {
            console.error('Processing error:', processingError);
            setStatusMessage('');
            setProcessing(false);
            showError('We couldn\'t analyze this call. Your file was uploaded — please try again or reach out to support if it keeps happening.');
            createAutoReport({
              errorType: 'evaluation_processing_failed',
              errorMessage: processingError.message || 'Cloud Function processing error',
              userEmail,
              userRole,
              teamId: userTeam,
              context: {
                callType: formData.callType,
                salesperson: formData.salesperson,
                prospect: formData.prospect,
                fileName: selectedFile?.name,
              },
            });
          }
        }
      );
    } catch (error) {
      console.error('❌ Upload error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      showError('Something went wrong uploading your file. Please refresh the page and try again.');
      setUploading(false);
      setProcessing(false);
      setStatusMessage('');
      createAutoReport({
        errorType: 'evaluation_upload_failed',
        errorMessage: error.message || error.code || 'Unknown upload error',
        userEmail,
        userRole,
        teamId: userTeam,
        context: {
          errorCode: error.code,
          fileName: selectedFile?.name,
          fileSize: selectedFile?.size,
          callType: formData.callType,
        },
      });
    }
  };
  
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="text-center py-8">
            <LoadingSpinner size="large" message="Loading..." />
          </div>
        </div>
      </div>
    );
  }

  if (!userEmail && !userId) {
    return (
      <div className="max-w-2xl mx-auto">
        <ErrorMessage 
          title="Authentication Required"
          message="You must be logged in to upload call recordings. Please refresh the page and log in."
          severity="error"
        />
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-4">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-2xl font-bold">Upload Call Recording</h2>
        </div>

        {/* Usage Status Display */}
        {subscription && usageData && !loadingUsage && (() => {
          const bonusCredits = subscription.addons?.bonusEvalCredits || 0;
          const usageStatus = getUsageStatus(
            subscription.plan,
            usageData.evaluationsUsed,
            bonusCredits,
            subscription.status
          );
          
          const statusDisplay = getSubscriptionStatusDisplay(subscription);
          const isLifetime = subscription.status === SUBSCRIPTION_STATUSES.LIFETIME;

          // Show current usage in a compact banner
          const showUsageBanner = !usageStatus.isLifetime && usageStatus.percentUsed > 0;
          
          if (usageStatus.warningLevel === 'blocked') {
            return (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-red-800 mb-2">🚫 Monthly Evaluation Limit Reached</p>
                    <p className="text-sm text-red-700 mb-3">
                      You've used {usageData.evaluationsUsed} of {usageStatus.limit} evaluations this month.
                      {bonusCredits > 0 && ` (includes ${bonusCredits} bonus credits)`}
                    </p>
                  </div>
                  <span className="text-2xl">📊</span>
                </div>
                
                <div className="mt-4 space-y-3">
                  <p className="font-medium text-red-800 text-sm">Get more evaluations:</p>
                  
                  {/* Credit Pack Options */}
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(ADDONS)
                      .filter(([key]) => key.startsWith('eval_pack'))
                      .map(([packId, pack]) => (
                        <button
                          key={packId}
                          type="button"
                          onClick={async () => {
                            try {
                              if (!isStripeConfigured()) {
                                showInfo('Payment system is being configured. Please contact support.');
                                return;
                              }
                              await purchaseEvalCredits(packId, userTeam, userEmail);
                            } catch (error) {
                              showError(error.message);
                            }
                          }}
                          className="flex items-center justify-between p-2 bg-white border border-red-200 rounded hover:bg-red-50 transition-colors text-sm"
                        >
                          <span className="font-medium text-gray-800">
                            {pack.credits} credits
                          </span>
                          <span className="text-green-600 font-semibold">
                            ${pack.price}
                          </span>
                        </button>
                      ))}
                  </div>
                  
                  <div className="text-sm text-red-700 pt-2 border-t border-red-200">
                    <p>Or go to <strong>Settings → Subscription</strong> to upgrade your plan.</p>
                  </div>
                </div>
              </div>
            );
          }

          if (usageStatus.warningLevel === 'critical') {
            return (
              <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-orange-800">⚠️ Running Low on Evaluations</p>
                    <p className="text-sm text-orange-700 mt-1">
                      {usageStatus.remaining} evaluation{usageStatus.remaining !== 1 ? 's' : ''} remaining this month
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => showInfo('Go to Settings → Subscription to purchase more credits or upgrade your plan.')}
                    className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors"
                  >
                    Get More
                  </button>
                </div>
              </div>
            );
          }

          if (usageStatus.warningLevel === 'warning') {
            return (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
                <p className="text-sm text-yellow-800">
                  📊 {usageStatus.remaining} evaluations remaining this month ({usageData.evaluationsUsed}/{usageStatus.limit} used)
                </p>
              </div>
            );
          }
          
          // Lifetime badge
          if (isLifetime) {
            return (
              <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2">
                <span className="text-lg">{statusDisplay.icon}</span>
                <span className="text-sm text-purple-800 font-medium">{statusDisplay.label}</span>
                <span className="text-sm text-purple-600">- {usageData.evaluationsUsed} evaluations used this month</span>
              </div>
            );
          }

          return null;
        })()}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Salesperson Selection */}
          {isBasicUser ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salesperson
              </label>
              <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-blue-50 text-blue-900">
                <span className="font-medium">
                  {userEmail || (loading ? 'Loading...' : 'Not available')}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Uploading your own call recording
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salesperson *
              </label>
              {loadingMembers ? (
                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                  Loading team members...
                </div>
              ) : loadMembersError ? (
                <div className="w-full px-4 py-2 border border-red-300 rounded-lg bg-red-50 text-red-800">
                  <p className="font-medium">❌ Error loading team members</p>
                  <p className="text-sm mt-1">{loadMembersError}</p>
                  <p className="text-sm mt-2">Try refreshing the page or contact support.</p>
                </div>
              ) : teamMembers.length > 0 ? (
                <select
                  required
                  value={formData.salesperson}
                  onChange={(e) => setFormData({ ...formData, salesperson: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={uploading || processing}
                >
                  <option value="">Select a team member...</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.email}>
                      {formatUserName(member)} {member.role !== 'basic' && `(${member.role})`}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-yellow-50 text-yellow-800">
                  No active team members found. Add users to your team first.
                </div>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Select the salesperson whose call you're evaluating
              </p>
            </div>
          )}
          
          {/* Prospect Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prospect Name *
            </label>
            <input
              type="text"
              value={formData.prospect}
              onChange={(e) => setFormData({...formData, prospect: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Carol"
              required
              disabled={uploading || processing}
            />
          </div>
          
          {/* Call Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Call Type *
            </label>
            <select
              value={formData.callType}
              onChange={(e) => setFormData({ ...formData, callType: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={uploading || processing}
            >
              {callTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Link to Lead (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link to Lead <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              value={linkedLeadId}
              onChange={(e) => setLinkedLeadId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={uploading || processing || loadingLeads}
            >
              <option value="">No linked lead</option>
              {recentLeads
                .filter(lead => {
                  // Filter by selected salesperson if one is chosen
                  if (!formData.salesperson) return true;
                  return lead.userEmail === formData.salesperson || lead.userId === formData.salesperson;
                })
                .map(lead => (
                  <option key={lead.id} value={lead.id}>
                    {lead.prospectName || lead.prospectPhone || 'Unnamed'} — {lead.timestamp?.toLocaleDateString() || 'N/A'}
                  </option>
                ))
              }
            </select>
            {loadingLeads && <p className="text-xs text-gray-400 mt-1">Loading leads...</p>}
          </div>

          {/* File Upload - Drag & Drop Zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Audio File *
            </label>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              className="sr-only"
              accept="audio/mp3,audio/mpeg,audio/wav,audio/x-m4a,audio/m4a,.mp3,.wav,.m4a"
              onChange={handleFileSelect}
              disabled={uploading || processing}
              aria-label="Select audio file"
            />
            <div 
              role="button"
              tabIndex={0}
              aria-label={selectedFile ? `Selected file: ${selectedFile.name}. Click to change file.` : 'Drop audio file here or click to browse'}
              onClick={handleDropZoneClick}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDropZoneClick(); } }}
              className={`mt-1 flex justify-center px-4 py-4 border-2 border-dashed rounded-md cursor-pointer transition-all duration-200 ${
                uploading || processing
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                  : isDragging 
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : selectedFile
                      ? 'border-green-400 bg-green-50 hover:border-green-500'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="space-y-2 text-center">
                {selectedFile ? (
                  /* File selected state */
                  <>
                    <div className="mx-auto flex items-center justify-center h-10 w-10 rounded-full bg-green-100">
                      <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatFileSize(selectedFile.size)} &middot; {selectedFile.type || 'audio file'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={removeSelectedFile}
                      className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium transition-colors"
                      aria-label="Remove selected file"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Remove
                    </button>
                  </>
                ) : (
                  /* Empty / drag state */
                  <>
                    <div className={`mx-auto flex items-center justify-center h-10 w-10 rounded-full transition-colors duration-200 ${
                      isDragging ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <svg
                        className={`h-5 w-5 transition-colors duration-200 ${isDragging ? 'text-blue-600' : 'text-gray-400'}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <div>
                      <p className={`text-sm font-medium transition-colors duration-200 ${isDragging ? 'text-blue-700' : 'text-gray-700'}`}>
                        {isDragging ? 'Drop your audio file here' : 'Drop audio file here or click to browse'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Supports MP3, WAV, M4A &middot; Max 100MB
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Upload Progress Bar */}
          {uploading && (
            <div className="mt-4">
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{statusMessage}</span>
                <span className="text-sm font-medium text-gray-700">{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Stage-based Processing Progress */}
          {processing && (
            <div className="mt-4 p-4 rounded-md" style={{ backgroundColor: colors.primary[50], border: `1px solid ${colors.primary[100]}` }}>
              <div className="space-y-3">
                {PROCESSING_STAGES.map((stage, index) => {
                  const isActive = index === processingStage;
                  const isComplete = index < processingStage;
                  
                  return (
                    <div key={stage.label} className="flex items-center gap-3">
                      {/* Stage indicator */}
                      <div className={`flex-shrink-0 flex items-center justify-center h-7 w-7 rounded-full text-sm transition-all duration-300 ${
                        isComplete
                          ? 'bg-green-100 text-green-600'
                          : isActive
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-gray-100 text-gray-400'
                      }`}>
                        {isComplete ? (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span>{stage.icon}</span>
                        )}
                      </div>
                      
                      {/* Stage label */}
                      <span className={`text-sm transition-colors duration-300 ${
                        isComplete
                          ? 'text-green-700 font-medium'
                          : isActive
                            ? 'text-blue-700 font-medium'
                            : 'text-gray-400'
                      }`}>
                        {stage.label}
                      </span>

                      {/* Active spinner */}
                      {isActive && (
                        <svg className="animate-spin h-4 w-4 text-blue-500 ml-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Overall progress bar */}
              <div className="mt-3 w-full bg-blue-100 rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-1000 ease-in-out"
                  style={{ 
                    width: `${PROCESSING_STAGES[processingStage]?.range[0] || 0}%`,
                    backgroundColor: colors.primary.main
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                This typically takes 2-5 minutes depending on call length
              </p>
            </div>
          )}
          
          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={uploading || processing || !selectedFile}
            loading={uploading || processing}
          >
            {uploading ? 'Uploading...' : processing ? 'Processing...' : 'Upload and Analyze Call'}
          </Button>
        </form>
        
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>📝 Note:</strong> After uploading, your call will be transcribed and analyzed.
            This typically takes 2-5 minutes depending on call length. You can view results in the
            Evaluations tab once processing is complete.
          </p>
        </div>
        
        <div className="mt-4 p-4 bg-purple-50 rounded-lg">
          <p className="text-sm text-purple-800">
            <strong>✨ Unlimited evaluations on your plan.</strong> Audio files are automatically deleted after 90 days.
          </p>
        </div>
      </div>
    </div>
  );
}

